import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button,
  Container,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import '../Styles/product.css';
import { useNavigate } from 'react-router-dom';
import { FaClipboardList, FaEdit, FaDownload, FaFolderOpen } from 'react-icons/fa';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { getAllCourses, getContentByCode, generateQRByNumber } from '../Servies/AdmiServices';
import TableData from '../components/common/TableData';

const QR_BASE_URL = 'https://img-qrcode.alrn.in/qr-images/';
const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.svg', '.bmp', '.tiff', '.tif'];

export default function Courses() {
  const headers = ['Board Name', 'Course', 'Display Name', 'Status', 'Action'];
  const navigate = useNavigate();
  const [courseData, setCourseData] = useState([]);

  // --- Get Content by Code dialog ---
  const [openContentDialog, setOpenContentDialog] = useState(false);
  const [hashedStr, setHashedStr] = useState('');
  const [contentResult, setContentResult] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentError, setContentError] = useState('');

  // --- Generate QR by Number dialog ---
  const [openGenQrDialog, setOpenGenQrDialog] = useState(false);
  const [genQrNumber, setGenQrNumber] = useState('');
  const [genQrLoading, setGenQrLoading] = useState(false);
  const [genQrError, setGenQrError] = useState('');

  // --- QR Images dialog ---
  const [openQrDialog, setOpenQrDialog] = useState(false);
  const [qrDirs, setQrDirs] = useState([]);
  const [qrDirsLoading, setQrDirsLoading] = useState(false);
  const [qrDirsError, setQrDirsError] = useState('');
  const [downloadingDir, setDownloadingDir] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  const handleEditEdit = (courseId) => {
    navigate(`/dashboard/CreateCourse?courseId=${courseId}`);
  };

  const handleStructure = (courseId) => {
    navigate(`/dashboard/CourseStructure?courseId=${courseId}`);
  };

  const getCoursesList = () => {
    getAllCourses()
      .then((res) => setCourseData(res?.data))
      .catch(() => {});
  };

  useEffect(() => {
    getCoursesList();
  }, []);

  // ---- Get Content by Code ----
  const handleOpenContentDialog = () => {
    setOpenContentDialog(true);
    setHashedStr('');
    setContentResult(null);
    setContentError('');
  };

  const handleCloseContentDialog = () => setOpenContentDialog(false);

  const handleFetchContent = async () => {
    if (!hashedStr.trim()) {
      setContentError('Please enter a hashed string.');
      return;
    }
    setContentLoading(true);
    setContentError('');
    setContentResult(null);
    try {
      const res = await getContentByCode(hashedStr.trim());
      if (res?.data?.success) {
        setContentResult(res.data);
      } else {
        setContentError(res?.data?.message || 'Failed to fetch content.');
      }
    } catch {
      setContentError('An error occurred while fetching content.');
    } finally {
      setContentLoading(false);
    }
  };

  // ---- Generate QR by Number ----
  const handleOpenGenQrDialog = () => {
    setOpenGenQrDialog(true);
    setGenQrNumber('');
    setGenQrError('');
  };

  const handleCloseGenQrDialog = () => {
    if (genQrLoading) return;
    setOpenGenQrDialog(false);
  };

  const handleGenerateQR = async () => {
    const num = parseInt(genQrNumber, 10);
    if (!genQrNumber || Number.isNaN(num) || num < 1) {
      setGenQrError('Please enter a valid positive number.');
      return;
    }
    setGenQrLoading(true);
    setGenQrError('');
    try {
      // Step 1: trigger server-side generation
      const response = await generateQRByNumber(num);
      if (!response.ok) throw new Error(`Server error (HTTP ${response.status})`);

      // Step 2: find the latest directory created on the image server
      const dirRes = await fetch(QR_BASE_URL);
      if (!dirRes.ok) throw new Error('Could not fetch directory listing after generation');
      const dirs = await dirRes.json();
      const latest = dirs
        .filter((d) => d.type === 'directory')
        .sort((a, b) => new Date(b.mtime) - new Date(a.mtime))[0];

      if (!latest) throw new Error('No directory found after generation');

      // Step 3: download its images as zip using same logic as folder list
      await buildAndDownloadZip(latest.name, () => {});
      setOpenGenQrDialog(false);
    } catch (err) {
      setGenQrError(`Failed: ${err.message}`);
    } finally {
      setGenQrLoading(false);
    }
  };

  // ---- QR Images ----
  const handleOpenQrDialog = async () => {
    setOpenQrDialog(true);
    setQrDirs([]);
    setQrDirsError('');
    setQrDirsLoading(true);
    try {
      const res = await fetch(QR_BASE_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const sorted = data
        .filter((item) => item.type === 'directory')
        .sort((a, b) => new Date(b.mtime) - new Date(a.mtime))
        .slice(0, 15);
      setQrDirs(sorted);
    } catch (err) {
      setQrDirsError(`Failed to load directories: ${err.message}`);
    } finally {
      setQrDirsLoading(false);
    }
  };

  const handleCloseQrDialog = () => {
    if (downloadingDir) return;
    setOpenQrDialog(false);
  };

  const buildAndDownloadZip = async (dirName, onProgress) => {
    const listRes = await fetch(`${QR_BASE_URL}${dirName}/`);
    if (!listRes.ok) throw new Error(`Could not list directory (HTTP ${listRes.status})`);
    const files = await listRes.json();

    const imageFiles = files.filter(
      (f) => f.type === 'file' && IMAGE_EXTENSIONS.some((ext) => f.name.toLowerCase().endsWith(ext))
    );

    if (imageFiles.length === 0) throw new Error(`No images found in "${dirName}".`);

    onProgress({ current: 0, total: imageFiles.length });

    const zip = new JSZip();
    const folder = zip.folder(dirName);

    await Promise.all(
      imageFiles.map(async (file) => {
        const imgRes = await fetch(`${QR_BASE_URL}${dirName}/${file.name}`);
        const blob = await imgRes.blob();
        folder.file(file.name, blob);
        onProgress((prev) => ({ ...prev, current: prev.current + 1 }));
      })
    );

    const zipBlob = await zip.generateAsync({ type: 'blob' });
    saveAs(zipBlob, `${dirName}.zip`);
  };

  const handleDownloadDirZip = async (dirName) => {
    setDownloadingDir(dirName);
    setDownloadProgress({ current: 0, total: 0 });
    try {
      await buildAndDownloadZip(dirName, setDownloadProgress);
    } catch (err) {
      alert(`Download failed: ${err.message}`);
    } finally {
      setDownloadingDir(null);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  // ---- Table columns ----
  const specificColumns = courseData.map((item) => ({
    'Board Name': item.boardName,
    Course: item.course,
    'Display Name': item.displayName,
    Status: item.status ? (
      <Button variant="contained" color="primary">Active</Button>
    ) : (
      <Button variant="contained" color="error">Inactive</Button>
    ),
    Action: (
      <div>
        <FaEdit
          style={{ cursor: 'pointer', marginRight: '10px' }}
          onClick={() => handleEditEdit(item.courseId)}
        />
        <FaClipboardList
          style={{ cursor: 'pointer', marginRight: '10px' }}
          onClick={() => handleStructure(item.courseId)}
        />
      </div>
    ),
  }));

  const content = contentResult?.content;
  const hashData = (() => {
    try {
      return contentResult?.hash_data ? JSON.parse(contentResult.hash_data) : null;
    } catch {
      return null;
    }
  })();

  const isDownloading = !!downloadingDir;

  return (
    <>
      <Helmet>
        <title> Dashboard: Courses </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Courses
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<FaFolderOpen />} onClick={handleOpenQrDialog}>
              QR Images
            </Button>
            <Button variant="outlined" onClick={handleOpenGenQrDialog}>
              Generate QR
            </Button>
            <Button variant="outlined" onClick={handleOpenContentDialog}>
              Get Content by Code
            </Button>
            <Button variant="contained" onClick={() => navigate('/dashboard/CreateCourse')}>
              Create Course
            </Button>
          </Stack>
        </Stack>

        <TableData data={specificColumns} headers={headers} rowsPerPageOptions={[5, 10, 20]} />
      </Container>

      {/* ---- Generate QR by Number Dialog ---- */}
      <Dialog open={openGenQrDialog} onClose={handleCloseGenQrDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Generate QR Codes by Number</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Number"
              type="number"
              value={genQrNumber}
              onChange={(e) => setGenQrNumber(e.target.value)}
              fullWidth
              inputProps={{ min: 1 }}
              placeholder="e.g. 3"
              error={!!genQrError}
              helperText={genQrError}
              disabled={genQrLoading}
            />
            {genQrLoading && (
              <Stack alignItems="center" py={1}>
                <CircularProgress size={28} />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Generating QR codes...
                </Typography>
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGenQrDialog} disabled={genQrLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleGenerateQR} disabled={genQrLoading}>
            Generate & Download
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- QR Images Dialog ---- */}
      <Dialog open={openQrDialog} onClose={handleCloseQrDialog} maxWidth="md" fullWidth>
        <DialogTitle>QR Image Directories</DialogTitle>
        <DialogContent>
          {qrDirsLoading && (
            <Stack alignItems="center" py={4}>
              <CircularProgress size={30} />
              <Typography variant="body2" color="text.secondary" mt={1}>
                Loading directories...
              </Typography>
            </Stack>
          )}

          {qrDirsError && (
            <Typography color="error" variant="body2" mt={1}>
              {qrDirsError}
            </Typography>
          )}

          {!qrDirsLoading && !qrDirsError && qrDirs.length === 0 && (
            <Typography variant="body2" color="text.secondary" mt={1}>
              No directories found.
            </Typography>
          )}

          {qrDirs.length > 0 && (
            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Directory Name</strong></TableCell>
                    <TableCell><strong>Modified Date</strong></TableCell>
                    <TableCell align="center"><strong>Download</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {qrDirs.map((dir) => {
                    const isThisDownloading = downloadingDir === dir.name;
                    return (
                      <TableRow key={dir.name} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <FaFolderOpen color="#f5a623" />
                            <Typography variant="body2">{dir.name}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(dir.mtime).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {isThisDownloading ? (
                            <Stack alignItems="center" spacing={0.5}>
                              <CircularProgress size={18} />
                              {downloadProgress.total > 0 && (
                                <>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(downloadProgress.current / downloadProgress.total) * 100}
                                    sx={{ width: 80 }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    {downloadProgress.current}/{downloadProgress.total}
                                  </Typography>
                                </>
                              )}
                            </Stack>
                          ) : (
                            <Tooltip title={`Download ${dir.name}.zip`}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleDownloadDirZip(dir.name)}
                                  disabled={isDownloading}
                                >
                                  <FaDownload />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseQrDialog} disabled={isDownloading}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Get Content by Code Dialog ---- */}
      <Dialog open={openContentDialog} onClose={handleCloseContentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Get Content by Hashed Code</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Hashed String"
              value={hashedStr}
              onChange={(e) => setHashedStr(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Paste the hashed string here..."
              error={!!contentError && !contentLoading}
              helperText={!contentLoading && contentError}
            />
            {contentLoading && (
              <Stack alignItems="center" py={2}>
                <CircularProgress size={28} />
              </Stack>
            )}

            {contentResult && content && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                {hashData && (
                  <>
                    <Stack direction="row" spacing={1} flexWrap="wrap" mb={1}>
                      {Object.entries(hashData).map(([k, v]) => (
                        <Chip key={k} label={`${k}: ${v}`} size="small" variant="outlined" />
                      ))}
                    </Stack>
                    <Divider sx={{ mb: 1.5 }} />
                  </>
                )}

                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  {content.contentName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {content.contentDescription}
                </Typography>

                <Stack spacing={0.5} mt={1}>
                  <InfoRow label="Content Code" value={content.contentCode} />
                  <InfoRow label="Display Name" value={content.contentDisplayName} />
                  <InfoRow
                    label="Type"
                    value={content.contentTypeCode === 1 ? 'AV (Audio/Video)' : content.contentTypeCode}
                  />
                  <InfoRow label="Sequence No" value={content.sequenceNo} />
                  <InfoRow label="Duration" value={`${content.duration}s`} />
                  <InfoRow
                    label="Status"
                    value={
                      <Chip
                        label={content.isActive ? 'Active' : 'Inactive'}
                        color={content.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    }
                  />
                </Stack>

                {content.fileURL && (
                  <Box mt={2}>
                    <Divider sx={{ mb: 1.5 }} />
                    <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                      Media URL
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        bgcolor: 'grey.100',
                        p: 1,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontFamily: 'monospace',
                      }}
                    >
                      {content.fileURL}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseContentDialog}>Close</Button>
          <Button variant="contained" onClick={handleFetchContent} disabled={contentLoading}>
            Fetch Content
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110 }}>
        {label}:
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography variant="body2">{value}</Typography>
      ) : (
        value
      )}
    </Stack>
  );
}
